/* ============================================================
   primitives.jsx — Avatar, Badge, ScoreChip, ProgressBar, EmptyState, KpiCard.

   Ported one-for-one from js/ui.js. Every class name is preserved verbatim so
   css/styles.css keeps matching without a single selector change (ADR 0013 §3).
   ============================================================ */

import { useState } from 'react'
import Icon from './icons'
import Chart from './Chart'
import Modal from './Modal'
import { describePermissionTag, parsePermissionDenial } from '../lib/errors'
import { avatarColor, initials as initialsOf } from '../data/seed'

/* `src` is a candidate's LinkedIn photo (a presigned S3 link from the API's
   avatar_url). Initials stay the fallback: no photo, or the link expired or
   failed to load. Keyed on src so a fresh link gets a fresh attempt.
   `viewable` makes a loaded photo a button that opens it full size (`viewSrc`, the
   full copy, when the API gives one; `src` is the small list copy); initials
   stay plain text since there is nothing bigger to show. */
export function Avatar({ name = '', initials, color, src, viewSrc, className = '', viewable = false }) {
  const label = typeof name === 'string' ? name : String(name ?? '')
  const bg = color || avatarColor(label)
  const text = (typeof initials === 'string' && initials) || initialsOf(label || '?')
  const [failedSrc, setFailedSrc] = useState(null)
  const [viewing, setViewing] = useState(false)
  const showPhoto = Boolean(src) && failedSrc !== src
  const img = <img src={src} alt={label} loading="lazy" decoding="async" onError={() => setFailedSrc(src)} />
  if (viewable && showPhoto) {
    return (
      <>
        <button
          type="button"
          className={`avatar avatar-photo avatar-viewable ${className}`}
          style={{ background: bg }}
          onClick={(e) => { e.stopPropagation(); setViewing(true) }}
          aria-label={label ? `View ${label}'s photo` : 'View photo'}
        >
          {img}
        </button>
        {/* Portal clicks still bubble through React to the row: stop them here. */}
        <span onClick={(e) => e.stopPropagation()}>
          <Modal open={viewing} title={label || 'Photo'} onClose={() => setViewing(false)}>
            <img className="avatar-view-img" src={viewSrc || src} alt={label} />
          </Modal>
        </span>
      </>
    )
  }
  return (
    <span className={`avatar ${showPhoto ? 'avatar-photo' : ''} ${className}`} style={{ background: bg }}>
      {showPhoto ? img : text}
    </span>
  )
}

export function AvatarStack({ names = [], max = 3 }) {
  const shown = names.slice(0, max)
  const extra = names.length - max
  return (
    <div className="avatar-stack">
      {shown.map((n, i) => (
        <Avatar key={`${n}-${i}`} name={n} />
      ))}
      {extra > 0 && <span className="more-count">+{extra}</span>}
    </div>
  )
}

// The 30-entry status -> class map from js/ui.js:73-81, verbatim.
export const STATUS_CLASS = {
  Open: 'b-green', Closed: 'b-gray', 'On Hold': 'b-amber', Draft: 'b-blue',
  Shortlist: 'b-blue', Screening: 'b-purple', Assessment: 'b-amber', Interview: 'b-indigo',
  Offer: 'b-teal', Hired: 'b-green', Rejected: 'b-red',
  Scheduled: 'b-blue', Completed: 'b-green', Cancelled: 'b-red', 'No Show': 'b-amber',
  Sent: 'b-blue', Accepted: 'b-green', Negotiating: 'b-amber', Declined: 'b-red', Expired: 'b-gray',
  'In Progress': 'b-amber', Pending: 'b-gray', Active: 'b-green', Invited: 'b-amber',
  'Strong Hire': 'b-green', Hire: 'b-teal', 'Lean Hire': 'b-amber', 'No Hire': 'b-red',
}

// Shared task/candidate priority map — Dashboard and Tasks each used to
// define an identical private copy.
export const PRIORITY_CLASS = { High: 'b-red', Medium: 'b-amber', Low: 'b-gray' }

/**
 * `title` is optional and usually unset. It exists for badges whose one-word
 * label is not self-explanatory — "Failed" on an Inbox row, for instance, where
 * the hover has to say that the CV text could not be read and the attachment is
 * still there to open by hand.
 *
 * Every other prop (style, data-tip, aria-*, …) is forwarded to the span. It used
 * to be dropped silently, so call sites' margins and tooltips never rendered.
 */
export function Badge({ children, className, title, ...rest }) {
  const cls = className || STATUS_CLASS[children] || 'b-gray'
  return <span {...rest} className={`badge ${cls}`} title={title}>{children}</span>
}

/**
 * A job's recruiter for display. When no recruiter is assigned the backend
 * falls back to the recruiter manager (`recruiter_is_hiring_manager`); the small
 * tag says so, so nobody reads the HM as an assigned recruiter.
 */
export function RecruiterName({ name, isManager = false, empty = '—' }) {
  if (!name) return empty
  return (
    <span className="recruiter-name">
      <span className="recruiter-name-text">{name}</span>
      {isManager && (
        <Badge
          className="b-gray badge-plain recruiter-hm-tag"
          data-tip="No recruiter assigned — the recruiter manager is covering this job"
        >
          Recruiter manager
        </Badge>
      )}
    </span>
  )
}

export function ScoreChip({ score }) {
  const n = Number(score)
  if (!Number.isFinite(n)) return null
  const pct = Math.min(100, Math.max(0, n))
  const shown = Math.round(n)
  // Theme tokens, not fixed hex — the old greens/blues dropped to ~2.6:1 on dark cards.
  // Same bands as the match labels (Strong ≥82 · Potential ≥65 · Weak), so a
  // ring never reads differently from the "Weak/Potential/Strong Match" beside it.
  const color =
    pct >= 82 ? 'var(--success)'
      : pct >= 65 ? 'var(--warning)' : 'var(--danger)'
  return (
    <span className="score">
      <span className="score-ring" style={{ '--pct': pct, '--sc-color': color }}>
        <span style={{ color }}>{shown}</span>
      </span>
    </span>
  )
}

export function ProgressBar({ pct, className }) {
  const c = pct >= 80 ? 'green' : pct >= 50 ? '' : pct >= 30 ? 'amber' : 'red'
  return (
    <div className="pbar">
      <div className={`pbar-fill ${className || c}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

/** Shimmer placeholder for a loading table/list — one row per record slot. */
export function SkeletonRows({ rows = 5 }) {
  return (
    <div role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton-row" aria-hidden="true" key={i}>
          <span className="skeleton skeleton-avatar" />
          <div className="flex-1">
            <span className="skeleton skeleton-line" style={{ width: `${52 - (i % 3) * 9}%` }} />
            <span className="skeleton skeleton-line" style={{ width: `${34 - (i % 3) * 6}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function EmptyState({ icon = 'search', title = 'No results found', children }) {
  // A 403 from require_permission reaches every screen as a load error; show it
  // as an access state rather than a raw "Missing required permission" string.
  const denied = typeof children === 'string' ? parsePermissionDenial(children) : null
  if (denied) {
    return (
      <div className="empty-state empty-state-restricted">
        <Icon name="lock" />
        <h3>Access restricted</h3>
        <p>
          Your role doesn’t include access to this section. If you need it, ask an administrator to
          grant the permission below.
        </p>
        <div className="empty-state-perms">
          {denied.map((tag) => (
            <span className="empty-state-perm" key={tag} title={tag}>{describePermissionTag(tag)}</span>
          ))}
        </div>
      </div>
    )
  }
  const body = children ?? 'Try adjusting your filters or search.'
  const isSimple = body == null || typeof body === 'string' || typeof body === 'number'
  return (
    <div className="empty-state">
      <Icon name={icon} />
      <h3>{title}</h3>
      {isSimple ? <p>{body}</p> : <div className="empty-state-body">{body}</div>}
    </div>
  )
}

/** Trend chip: `dir` is 'up' | 'down' | 'flat', matching js/dashboard.js:21-26. */
export function Trend({ dir, arrow, children }) {
  if (dir === 'flat') return <span className="trend trend-flat">{children}</span>
  // `dir` is goodness (colour); `arrow` is the data direction when the two
  // differ — a falling time-to-hire is good (green) but the icon must point
  // down, or the chip contradicts its own "-3 days" text.
  const icon = arrow || dir
  return (
    <span className={`trend ${dir === 'up' ? 'trend-up' : 'trend-down'}`}>
      <Icon name={icon === 'up' ? 'trending-up' : 'trending-down'} />
      {children}
    </span>
  )
}

/**
 * The KPI card repeated across dashboard (8), interviews, jobboard and
 * recruiterhub. Markup is exactly js/dashboard.js:27-35 so the frozen CSS
 * matches: .kpi > .kpi-top > (.kpi-label + .kpi-icn), .kpi-value, .kpi-foot.
 */
export function KpiCard({ icon, tone = 'i-indigo', label, value, foot, trend, dir = 'up' }) {
  return (
    <div className="kpi">
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        <span className={`kpi-icn ${tone}`}>
          <Icon name={icon} />
        </span>
      </div>
      <div className="kpi-value">{value}</div>
      {(trend || foot) && (
        <div className="kpi-foot">
          {trend && <Trend dir={dir}>{trend}</Trend>}
          {foot && <span className="kpi-foot-text">{foot}</span>}
        </div>
      )}
    </div>
  )
}

/**
 * Compact dashboard tile. Sibling of KpiCard, not a variant — Interviews,
 * JobBoard and RecruiterHub keep the 40px icon card and the 4-up `.g-kpi` row.
 * Sparkline args are positional (charts.js sparkline(canvas, data, color)), so
 * `spark` is a number[] and `sparkColor` is a color string. A 1-element array
 * divides by zero in the engine; the length guard is load-bearing.
 */
export function KpiTile({ label, value, trend, dir = 'flat', arrow, spark, sparkColor }) {
  return (
    <div className="kpi kpi-tile">
      <span className="kpi-label">{label}</span>
      <div className="kpi-value">{value}</div>
      {trend && <Trend dir={dir} arrow={arrow}>{trend}</Trend>}
      {spark?.length > 1 && (
        <div className="kpi-spark">
          <Chart type="sparkline" data={spark} options={sparkColor} height={36} />
        </div>
      )}
    </div>
  )
}

export function FieldError({ children }) {
  return <span className={`field-error${children ? ' show' : ''}`}>{children || ''}</span>
}

/** 1–5 star input (interview scorecards, profile rating). */
export function Stars({ value, onChange, disabled }) {
  const set = (n) => { if (!disabled) onChange(n) }
  return (
    <div className="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`rs${n <= value ? ' on' : ''}`}
          onClick={() => set(n)}
          role="radio"
          aria-label={`${n} out of 5 stars`}
          aria-checked={n === value}
          aria-disabled={Boolean(disabled)}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); set(n) } }}
        >
          <Icon name="star" />
        </span>
      ))}
    </div>
  )
}

export { Icon }
